import { useRef, useState } from 'react'
import { ASSETS, LINKS } from './shopData'
import { ShopButton, Reveal } from './ShopUI'
import TurnstileField from '@/components/TurnstileField'
import { isTurnstileConfigured, subscribeToUpdates } from '@/services/siteEmailService'

// "Wins are better together" — one dark section over the football art:
// centered headline + copy with the early-access email capture inside it,
// mirroring the Shopify original. Submitting hands the address to the
// site-email edge function, which mails a confirmation link; the welcome and
// the info@getdub.com notice follow once the link is used.
export function ShopTogether() {
  const [email, setEmail] = useState('')
  const [agree, setAgree] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const turnstileRef = useRef(null)

  const submit = async (e) => {
    e.preventDefault()
    if (!email || sending) return
    setError('')

    // The checkbox isn't `required`, so without this the submit silently
    // no-ops and the form looks broken.
    if (!agree) {
      setError('Please accept the privacy policy to continue.')
      return
    }

    if (isTurnstileConfigured() && !captchaToken) {
      setError('Please complete the verification challenge before continuing.')
      return
    }

    setSending(true)
    try {
      await subscribeToUpdates(email, captchaToken)
      setDone(true)
    } catch (err) {
      // Tokens are single-use — reset so a retry doesn't reuse a spent one.
      try { turnstileRef.current?.reset() } catch { /* widget not mounted */ }
      setCaptchaToken('')
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <section
      className="relative overflow-hidden bg-[#181521] bg-cover bg-center py-24 text-white sm:py-32"
      style={{ backgroundImage: `url('${ASSETS.bgFootball}')` }}
    >
      <div className="absolute inset-0 bg-[#241f33]/80" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          {/* Type + color set inline: the design system's unlayered h2 rule
              beats Tailwind's layered utilities. */}
          <h2
            className="font-[var(--font-heading)] font-extrabold"
            style={{ color: '#ffffff', fontSize: 'clamp(2.75rem, 5.5vw, 4.5rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            Wins are better together
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-white/70">
            With dub, competition brings people together. Whether you’re here to follow your
            instincts, study every matchup, or just play along for fun, there’s room for everyone.
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-12">
          <h3 className="font-[var(--font-heading)] text-lg font-bold" style={{ color: '#ffffff' }}>
            Get early access to new tournaments and fun prizes
          </h3>
          {done ? (
            <p className="mt-6 text-lg font-medium text-[#EBFF59]">
              Almost there — check your inbox and tap the link to confirm.
            </p>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-5 max-w-lg text-left">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="h-12 flex-1 rounded-lg border border-white/20 bg-white px-4 text-[#292237] outline-none transition-colors placeholder:text-[#66626E]/70 focus:border-[#EBFF59]"
                />
                <ShopButton
                  variant="primary"
                  type="submit"
                  className="h-12 !px-7"
                  disabled={sending}
                >
                  {sending ? 'Signing up…' : 'Sign up'}
                </ShopButton>
              </div>
              <label className="mt-4 flex items-center gap-2.5 text-sm text-white/90">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="h-4 w-4 rounded border-white/40 accent-[#EBFF59]"
                />
                <span>
                  I have read and accept the{' '}
                  <a
                    href={LINKS.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-white"
                  >
                    privacy policy
                  </a>
                  .
                </span>
              </label>
              {/* Dark section — Turnstile's dark theme reads better here than
                  the light widget used on the app's own forms. */}
              <TurnstileField
                ref={turnstileRef}
                onToken={setCaptchaToken}
                theme="dark"
                className="mt-4"
              />
              {error && (
                <p className="mt-3 text-center text-sm text-[#ffb4b4]">{error}</p>
              )}
            </form>
          )}
        </Reveal>
      </div>
    </section>
  )
}

// Glassy mini-bracket decoration for the final CTA's edges. Drawn once and
// mirrored with scale-x for the right side.
function GlassBracket({ className = '' }) {
  // A real two-round bracket: two matches (pairs of small pills) at the
  // screen edge whose elbows converge into a stacked pair of large pills
  // in the vertical middle.
  return (
    <svg
      viewBox="0 0 340 480"
      aria-hidden="true"
      className={`pointer-events-none absolute w-[340px] ${className}`}
    >
      <g stroke="#2b2531" strokeOpacity="0.5" strokeWidth="1.5" fill="none">
        <path d="M70 62 h30 v64 h-30" />
        <path d="M100 94 h15 v110 h15" />
        <path d="M70 358 h30 v64 h-30" />
        <path d="M100 390 h15 v-114 h15" />
      </g>
      <g fill="rgba(255,255,255,0.4)">
        <rect x="-60" y="40" width="130" height="44" rx="22" />
        <rect x="-60" y="104" width="130" height="44" rx="22" />
        <rect x="130" y="176" width="210" height="56" rx="28" />
        <rect x="130" y="248" width="210" height="56" rx="28" />
        <rect x="-60" y="336" width="130" height="44" rx="22" />
        <rect x="-60" y="400" width="130" height="44" rx="22" />
      </g>
    </svg>
  )
}

// Final call to action — bright lemon gradient with glass brackets at the
// edges, dark headline, deep-purple CTA (mirrors the Shopify original).
export function ShopFinalCTA() {
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(100deg,#EBFF59_0%,#dbf76c_45%,#c8f0a2_100%)] py-24 text-center sm:py-32">
      <GlassBracket className="left-0 top-1/2 hidden -translate-y-1/2 md:block" />
      <GlassBracket className="right-0 top-1/2 hidden -translate-y-1/2 scale-x-[-1] md:block" />

      <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          {/* Type set inline: the design system's unlayered h2 rule wins over
              Tailwind's layered utilities. */}
          <h2
            className="font-[var(--font-heading)] font-extrabold text-[#211b29]"
            style={{ fontSize: 'clamp(2.75rem, 5.5vw, 4.5rem)', lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Ready to get your dub?
          </h2>
          <p className="mt-5 text-[#292237]">Winning feels good. Go get it.</p>
          <div className="mt-8">
            <ShopButton variant="purple" href={LINKS.register}>
              Make your picks
            </ShopButton>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
